import { forwardRef, PropsWithChildren, useLayoutEffect, useRef, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import * as Accordion from "@radix-ui/react-accordion";
import { Link } from "react-router-dom";
import clsx from "clsx";
import { useElementSize } from "react-migration/lib/util/useElementSize";
import { Notification } from "react-migration/components/Notification";
import { logEventV2, WorkbenchEventName } from "react-migration/lib/util/logEventV2";
import { Icon } from "react-migration/components/Icon";
import { Tags } from "react-migration/components/Tags";
import { useKeyIndicatorContext, KeyIndicatorTagsProvider } from "react-migration/lib/map/useKeyIndicatorTags";
import { InsightListItemVisual } from "../InsightListItemVisual";

type InsightsSummaryAccordionItemProps = Pick<Accordion.AccordionItemProps, "children"> & {
  value: string;
  title: string;
  icon: string;
  tabId?: string;
  tabName?: string;
  loading?: boolean;
  expanded?: boolean;
  hideLink?: boolean;
};

type InsightsSummaryAccordionItemHeaderProps = Pick<
  InsightsSummaryAccordionItemProps,
  "icon" | "tabName" | "title" | "loading" | "expanded"
>;

function InsightsSummaryAccordionItemHeader({
  icon,
  tabName,
  title,
  loading,
  expanded,
}: InsightsSummaryAccordionItemHeaderProps) {
  const { tags } = useKeyIndicatorContext();
  const quickInsightCols = localStorage.getItem("quickInsightCols") === "true";

  const rightAlignedElements = loading ? (
    <div className={clsx("relative pr-6 flex items-center cursor-default", quickInsightCols && "pt-2")}>
      <Icon
        icon="icon-lt-loader-4-line"
        size="md"
        extraClasses={clsx(
          "inline-block animate-spin origin-[50.5%_51%] transition absolute text-blue-grey-300",
          loading ? "opacity-100" : "opacity-0"
        )}
      />
    </div>
  ) : (
    <>
      <div className="flex items-center shrink-0">
        <Tags tags={tags} size={quickInsightCols ? "sm" : "md"} placeholder="" />
      </div>
      {expanded !== undefined && (
        <div className={clsx("items-center ml-3", quickInsightCols ? "hidden" : "flex")}>
          <Icon size="md" cursor="pointer" icon={expanded ? "icon-lt-arrow-up-s-line" : "icon-lt-arrow-down-s-line"} />
        </div>
      )}
    </>
  );

  return (
    <Accordion.Header>
      <Accordion.Trigger className="w-full">
        <InsightListItemVisual
          icon={<Icon icon={icon} cursor="pointer" />}
          tabName={tabName}
          title={title}
          print={expanded === undefined}
        >
          {rightAlignedElements}
        </InsightListItemVisual>
      </Accordion.Trigger>
    </Accordion.Header>
  );
}

type InsightsSummaryAccordionItemContentProps = PropsWithChildren<
  Pick<InsightsSummaryAccordionItemProps, "expanded" | "title">
>;

function InsightsSummaryAccordionItemContent({ children, title, expanded }: InsightsSummaryAccordionItemContentProps) {
  const [resizeRef] = useElementSize();
  const [isEmpty, setIsEmpty] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // Check if the rendered content is empty after mounting and when children change
  useLayoutEffect(() => {
    if (contentRef.current) {
      // Get the div that contains the children
      const childDiv = contentRef.current;

      // Check if it has any child nodes or only contains whitespace
      const hasVisibleContent =
        childDiv.childNodes.length > 0 && (childDiv.textContent ? childDiv.textContent.trim().length > 0 : false);

      setIsEmpty(!hasVisibleContent);
    }
  }, [children]);

  return (
    <Accordion.Content className="overflow-hidden" forceMount>
      <div className="overflow-hidden transition-all duration-300 ease-in-out" style={expanded ? {} : { height: 0 }}>
        <div ref={resizeRef} className={clsx("px-4", !isEmpty && "pt-[10px] pb-4")}>
          <ErrorBoundary
            fallback={
              <Notification.Error
                title="An error occurred"
                message={`There was an issue loading the ${title} summary.`}
              />
            }
          >
            <div ref={contentRef}>{children}</div>
          </ErrorBoundary>
        </div>
      </div>
    </Accordion.Content>
  );
}

const InsightsSummaryAccordionItemInner = forwardRef<HTMLDivElement, InsightsSummaryAccordionItemProps>(
  ({ value, title, tabId, tabName, icon, loading, expanded, hideLink = false, children }, forwardedRef) => {
    const showLink = !hideLink && !!(tabId && tabName);
    const quickInsightCols = localStorage.getItem("quickInsightCols") === "true";
    return (
      <Accordion.Item
        className={clsx(
          "group overflow-hidden rounded-xl border border-neutral-200",
          quickInsightCols ? "col-span-1" : "col-span-2",
          quickInsightCols && expanded && "col-span-2"
        )}
        value={value}
        ref={forwardedRef}
        data-testid={`insight-item-${value}`}
      >
        <InsightsSummaryAccordionItemHeader
          title={title}
          tabName={tabName}
          icon={icon}
          loading={loading}
          expanded={expanded}
        />
        <InsightsSummaryAccordionItemContent title={title} expanded={expanded ?? true}>
          {children}
          {showLink && (
            <div className="pt-4">
              <Link
                className={clsx(
                  "group leading-none select-none no-underline!",
                  expanded !== undefined && "cursor-pointer"
                )}
                onClick={() =>
                  logEventV2({
                    name: WorkbenchEventName.WORKBENCH_CARD_CLICK,
                    properties: { cardName: "Insights", action: "goTo", clickDetail: title },
                  })
                }
                to={`/assessment/${tabId}`}
              >
                <span className="mr-1 underline">Go to {tabName} tab</span>
                <Icon
                  icon="icon-lt-arrow-right-line"
                  extraClasses={expanded !== undefined ? "group-hover:opacity-50" : ""}
                  size="sm"
                  cursor="pointer"
                />
              </Link>
            </div>
          )}
        </InsightsSummaryAccordionItemContent>
      </Accordion.Item>
    );
  }
);

InsightsSummaryAccordionItemInner.displayName = "InsightsSummaryAccordionItemInner";

export const InsightsSummaryAccordionItem = forwardRef<HTMLDivElement, InsightsSummaryAccordionItemProps>(
  (props, forwardedRef) => (
    <KeyIndicatorTagsProvider>
      <InsightsSummaryAccordionItemInner {...props} ref={forwardedRef} />
    </KeyIndicatorTagsProvider>
  )
);

InsightsSummaryAccordionItem.displayName = "InsightsSummaryAccordionItem";
